import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, TrophyIcon, ShareIcon } from '@heroicons/react/24/outline'
import { SparklesIcon } from '@heroicons/react/24/solid'
import ConfettiEffect from './ConfettiEffect'

export default function CelebrationModal({ 
  isOpen, 
  onClose, 
  badges = [], 
  title = "Amazing Achievement!",
  message = "You've unlocked new badges!",
  confettiType = 'stars'
}) {
  const totalPoints = badges.reduce((sum, badge) => sum + badge.points, 0)

  const handleShare = (platform) => {
    const shareText = `I just earned ${badges.length} new badge${badges.length > 1 ? 's' : ''} and ${totalPoints} points in the AI Prompting & Agent Systems course! 🎉`
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}`
    }

    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400')
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {isOpen && <ConfettiEffect type={confettiType} />}
        
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-gradient-to-br from-white to-gray-50 p-6 text-left align-middle shadow-2xl transition-all border-4 border-yellow-400">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>

                {/* Header with trophy animation */}
                <div className="text-center mb-6">
                  <div className="relative inline-block">
                    <TrophyIcon className="h-20 w-20 text-yellow-500 mx-auto animate-bounce" />
                    <SparklesIcon className="h-8 w-8 text-yellow-400 absolute -top-2 -right-2 animate-ping" />
                    <SparklesIcon className="h-6 w-6 text-yellow-400 absolute -bottom-2 -left-2 animate-ping" style={{ animationDelay: '0.3s' }} />
                  </div>
                  
                  <Dialog.Title
                    as="h3"
                    className="text-3xl font-bold text-gray-900 mt-4 bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent"
                  >
                    {title}
                  </Dialog.Title>
                  <p className="text-gray-600 mt-2">{message}</p>
                </div>

                {/* Badges earned */}
                <div className="space-y-3 mb-6 max-h-60 overflow-y-auto">
                  {badges.map((badge, index) => (
                    <div
                      key={badge.id || index}
                      className="flex items-center p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border-2 border-yellow-200 transform transition-all hover:scale-105"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="text-3xl mr-3">{badge.icon}</div>
                      <div className="flex-1">
                        <p className="font-bold text-gray-900">{badge.name}</p>
                        <p className="text-sm text-gray-600">{badge.description}</p>
                      </div>
                      <div className="text-right ml-3">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-yellow-500 text-white">
                          +{badge.points}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total points */}
                <div className="bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg p-4 mb-6 text-center">
                  <p className="text-sm font-semibold opacity-90">Total Points Earned</p>
                  <p className="text-3xl font-bold">+{totalPoints}</p>
                </div>

                {/* Share buttons */}
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 text-center font-semibold mb-2">
                    <ShareIcon className="h-4 w-4 inline mr-1" />
                    Share Your Achievement
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      onClick={() => handleShare('twitter')}
                      className="px-4 py-2 bg-blue-400 hover:bg-blue-500 text-white rounded-lg font-semibold text-sm transition-colors"
                    >
                      Twitter
                    </button>
                    <button
                      onClick={() => handleShare('linkedin')}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm transition-colors"
                    >
                      LinkedIn
                    </button>
                    <button
                      onClick={() => handleShare('facebook')}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold text-sm transition-colors"
                    >
                      Facebook
                    </button>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={onClose}
                  className="mt-4 w-full px-4 py-3 bg-gradient-to-r from-cte-blue to-cte-blue-dark text-white rounded-lg font-bold hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  Continue Learning
                </button>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
